import { useEffect, useState, useCallback, useRef } from "react";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { HttpMethodProps } from "../../types";
import { STORAGE_TOKEN_KEY } from "../../contexts/AuthContext";

interface RequestOptions {
  method: HttpMethodProps;
  route: string;
  body?: any;
}
function usePerformRequest<Type>({ method, route, body }: RequestOptions) {
  const [data, setData] = useState<Type>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError>();
  const [status, setStatus] = useState<number | string>("undefined");
  const [isLoading, setLoading] = useState<boolean>(false);
  
  // Use refs to store latest values without causing re-renders
  const methodRef = useRef(method);
  const bodyRef = useRef(body);
  methodRef.current = method;
  bodyRef.current = body;

  const reloadData = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_TOKEN_KEY);
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: methodRef.current,
      data: bodyRef.current,
      url: route,
    };
    setLoading(true);
    const r: any = await axios.request(config).catch((e: AxiosError) => {
      setError(e);
      setStatus(e.response?.status as number);

      return {
        status: e.response?.status,
      };
    });
    setResponse(r as AxiosResponse);
    setStatus(r.status);
    setLoading(false);
    setData(r?.data ?? undefined);
  }, [route]);
  
  useEffect(() => {
    if (!route) {
      setLoading(false);
      setData(undefined);
      return;
    }
    reloadData().catch(() => setLoading(false));
  }, [route, reloadData]);

  return { data, response, isLoading, reloadData, status, error };
}

interface PerformRequestOptions {
  method: HttpMethodProps;
  data?: any;
  route: string;
  headers?: any;
  doNotUseAuthorization?: boolean;
  onError?: () => any;
}

async function PerformRequest<Type>({
  method,
  data,
  route,
  headers,
  doNotUseAuthorization,
  onError,
}: PerformRequestOptions) {
  const token = localStorage.getItem(STORAGE_TOKEN_KEY);

  const requestHeaders = doNotUseAuthorization
    ? { ...headers }
    : { Authorization: `Bearer ${token}`, ...headers };
  const config = {
    method,
    data,
    url: route,
    headers: requestHeaders,
  };

  const r: any = await axios.request(config).catch((e: AxiosError) => {
    if (onError) onError();
    return {
      status: e.response?.status,
      message: (e?.response?.data as any)?.message,
      data: (e?.response?.data as any)?.data,
    };
  });
  return {
    status: r.status as number,
    data: r?.data as Type,
    message: r?.message ?? undefined,
  };
}

export { usePerformRequest, PerformRequest };
